import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  MegaphoneIcon, 
  RocketLaunchIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftIcon,
  DocumentChartBarIcon,
  CpuChipIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
  Squares2X2Icon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useAmazonProfile } from '../contexts/AmazonProfileContext';
import { useTheme } from '../contexts/ThemeContext';
import { useOptimizedStoreSwitch } from '../hooks/useOptimizedStoreSwitch';
import { useClearPageCacheOnStoreChange } from '../hooks/usePageCache';
import StoreSelector from './StoreSelector';
import UserAccountMenu from './UserAccountMenu';
import Logo from './ui/Logo';
import {
  ADVERTISING_NAV,
  CAMPAIGNS_DEFAULT_PATH,
  isAdvertisingChildActive,
  isCampaignsSection,
} from '../constants/advertisingNav';
import { prefetchCampaignHubRoute } from '../utils/prefetchCampaignHub';
import { typography } from '../constants/typography';
import {
  isPlatformAdmin,
  canAccessUserManagement,
  NAV_FEATURE_MAP,
  userHasFeature,
} from '../constants/features';
import PendingApprovalBanner from './PendingApprovalBanner';
import SubscriptionStatusBanner from './dashboard/SubscriptionStatusBanner';
import DashboardAssistantChat from './dashboard/DashboardAssistantChat';
import RouteErrorBoundary from './ui/RouteErrorBoundary';
import { handleSpaNavClick } from '../utils/spaNavigation';
import { SELLERCLUB_NAV } from '../constants/sellerclubBrand';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Products', href: '/products', icon: ShoppingBagIcon },
  {
    name: 'Campaigns',
    href: CAMPAIGNS_DEFAULT_PATH,
    icon: MegaphoneIcon,
    children: ADVERTISING_NAV,
  },
  { name: 'Campaign Launcher', href: '/campaign-launcher', icon: RocketLaunchIcon },
  { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
  { name: 'Category performance', href: '/category-performance', icon: Squares2X2Icon },
  { name: 'Categorization', href: '/agency/text-categorization', icon: Squares2X2Icon },
  { name: 'Sellerclub Automation', href: '/automation', icon: CpuChipIcon },
  { name: 'Agentic AI', href: '/agentic-ai', icon: SparklesIcon, badge: 'AI' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const SIDEBAR_EXPANDED_STORAGE_KEY = 'sellerclub.sidebarExpanded';
const HEADER_SHOW_STORE_KEY = 'sellerclub.headerShowStoreName';

function isNavItemActive(item, pathname) {
  if (item.children) {
    return isCampaignsSection(pathname);
  }
  if (item.href === '/agency/text-categorization') {
    return pathname === '/agency' || pathname.startsWith('/agency/');
  }
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

function compactNavTooltipLabel(item) {
  if (item.name === 'Campaigns') return 'Campaign manager';
  return item.name;
}

/** Cursor-adjacent label on the collapsed rail (no browser link preview). */
function NavTooltip({ label, show, isDarkMode, children }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  if (!show) return children;

  const tooltipClass = classNames(
    'pointer-events-none fixed z-[200] whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium shadow-lg border',
    isDarkMode
      ? 'bg-slate-800 text-slate-100 border-slate-600'
      : 'bg-white text-slate-800 border-slate-200'
  );

  return (
    <>
      <div
        className="w-full"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onMouseMove={(e) => setPos({ x: e.clientX + 14, y: e.clientY + 10 })}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
      >
        {children}
      </div>
      {visible && typeof document !== 'undefined'
        && createPortal(
          <span role="tooltip" className={tooltipClass} style={{ left: pos.x, top: pos.y }}>
            {label}
          </span>,
          document.body
        )}
    </>
  );
}

function CompactSidebarIcon({
  item,
  isActive,
  isDarkMode,
  iconClass,
  activeParentClass,
  inactiveParentClass,
  tooltipLabel,
  showNavTooltips,
  onPress,
  activeIconClass,
}) {
  const defaultActive = isDarkMode ? SELLERCLUB_NAV.iconActiveDark : SELLERCLUB_NAV.iconActiveLight;
  const defaultInactive = isDarkMode
    ? SELLERCLUB_NAV.iconInactiveDark
    : SELLERCLUB_NAV.iconInactiveLight;

  return (
    <NavTooltip label={tooltipLabel} show={showNavTooltips} isDarkMode={isDarkMode}>
      <button
        type="button"
        onClick={() => onPress(item.href)}
        onMouseEnter={() => {
          if (item.children?.length) {
            prefetchCampaignHubRoute(CAMPAIGNS_DEFAULT_PATH);
            item.children.forEach((child) => prefetchCampaignHubRoute(child.path));
          }
        }}
        onFocus={() => {
          if (item.children?.length) {
            prefetchCampaignHubRoute(CAMPAIGNS_DEFAULT_PATH);
            item.children.forEach((child) => prefetchCampaignHubRoute(child.path));
          }
        }}
        className={classNames(
          isActive ? activeParentClass : inactiveParentClass,
          'relative w-full group flex items-center justify-center rounded-lg p-2.5 transition-colors duration-200',
          isActive && item.children && 'ring-2 ring-orange-500/30'
        )}
        aria-label={tooltipLabel}
      >
        <item.icon
          className={classNames(iconClass, isActive ? (activeIconClass || defaultActive) : defaultInactive)}
        />
        {item.badge && (
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-orange-500 ring-2 ring-white dark:ring-slate-900" />
        )}
      </button>
    </NavTooltip>
  );
}

function SidebarNavItems({
  items,
  pathname,
  isDarkMode,
  campaignsExpanded,
  onToggleCampaigns,
  onCampaignsNavigate,
  navigate,
  size = 'desktop',
  compact = false,
  showNavTooltips = false,
}) {
  const isMobile = size === 'mobile';
  const iconClass = compact ? 'h-5 w-5' : (isMobile ? 'h-[18px] w-[18px]' : 'h-4 w-4');

  const activeParentClass = isDarkMode
    ? SELLERCLUB_NAV.activeParentDark
    : SELLERCLUB_NAV.activeParentLight;
  const inactiveParentClass = isDarkMode
    ? SELLERCLUB_NAV.inactiveParentDark
    : SELLERCLUB_NAV.inactiveParentLight;
  const activeChildClass = isDarkMode
    ? SELLERCLUB_NAV.activeChildDark
    : SELLERCLUB_NAV.activeChildLight;
  const inactiveChildClass = isDarkMode
    ? SELLERCLUB_NAV.inactiveChildDark
    : SELLERCLUB_NAV.inactiveChildLight;

  return items.map((item) => {
    const isActive = isNavItemActive(item, pathname);

    if (item.children) {
      if (compact) {
        return (
          <CompactSidebarIcon
            key={item.name}
            item={item}
            isActive={isActive}
            isDarkMode={isDarkMode}
            iconClass={iconClass}
            activeParentClass={activeParentClass}
            inactiveParentClass={inactiveParentClass}
            tooltipLabel={compactNavTooltipLabel(item)}
            showNavTooltips={showNavTooltips}
            onPress={onCampaignsNavigate}
            activeIconClass={isDarkMode ? SELLERCLUB_NAV.iconActiveDark : SELLERCLUB_NAV.iconActiveLight}
          />
        );
      }

      return (
        <div key={item.name} className="space-y-0.5">
          <div
            className={classNames(
              isActive ? activeParentClass : inactiveParentClass,
              'w-full group flex items-center rounded-lg transition-colors duration-200',
              isMobile ? 'px-2 py-1.5' : 'px-2 py-1.5'
            )}
          >
            <a
              href={item.href}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                onCampaignsNavigate(item.href);
              }}
              className="flex flex-1 min-w-0 items-center gap-0 rounded-md no-underline"
            >
              <span className="p-1.5 rounded-md shrink-0 transition-colors hover:opacity-80">
                <item.icon
                  className={classNames(
                    iconClass,
                    isActive
                      ? (isDarkMode ? SELLERCLUB_NAV.iconActiveDark : 'text-orange-200')
                      : (isDarkMode ? 'text-slate-400' : 'text-slate-500')
                  )}
                />
              </span>
              <span
                className={classNames(
                  'flex-1 min-w-0 text-left px-1 py-1.5 truncate',
                  typography.sidebarParent,
                  isActive
                    ? (isDarkMode ? 'text-slate-100' : 'text-white')
                    : (isDarkMode ? 'text-slate-300' : 'text-slate-700')
                )}
              >
                {item.name}
              </span>
            </a>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleCampaigns();
              }}
              className={classNames(
                'p-1.5 rounded-md shrink-0 transition-colors',
                isDarkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-black/10 text-slate-500',
                isActive && !isDarkMode && 'hover:bg-white/15 text-white/90'
              )}
              aria-label={campaignsExpanded ? 'Collapse campaigns menu' : 'Expand campaigns menu'}
              aria-expanded={campaignsExpanded}
            >
              <ChevronDownIcon
                className={classNames(
                  'h-4 w-4 transition-transform duration-200',
                  campaignsExpanded ? 'rotate-180' : ''
                )}
              />
            </button>
          </div>
          {campaignsExpanded && (
            <div className={classNames('space-y-0.5 pb-1', isMobile ? 'ml-3' : 'ml-2')}>
              {item.children.map((child) => {
                const childActive = isAdvertisingChildActive(child, pathname);
                return (
                  <a
                    key={child.key}
                    href={child.path}
                    onMouseEnter={() => prefetchCampaignHubRoute(child.path)}
                    onFocus={() => prefetchCampaignHubRoute(child.path)}
                    onClick={(e) => handleSpaNavClick(e, navigate, child.path)}
                    className={classNames(
                      childActive ? activeChildClass : inactiveChildClass,
                      'block w-full text-left rounded-md pl-7 pr-2.5 py-1.5 transition-colors duration-150 no-underline',
                      typography.sidebarChild
                    )}
                  >
                    {child.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (compact) {
      return (
        <CompactSidebarIcon
          key={item.name}
          item={item}
          isActive={isActive}
          isDarkMode={isDarkMode}
          iconClass={iconClass}
          activeParentClass={activeParentClass}
          inactiveParentClass={inactiveParentClass}
          tooltipLabel={compactNavTooltipLabel(item)}
          showNavTooltips={showNavTooltips}
          onPress={(href) => navigate(href)}
        />
      );
    }

    return (
      <a
        key={item.name}
        href={item.href}
        onClick={(e) => handleSpaNavClick(e, navigate, item.href)}
        className={classNames(
          isActive ? activeParentClass : inactiveParentClass,
          'w-full group flex items-center rounded-lg transition-colors duration-200 no-underline',
          typography.sidebarParent,
          isMobile ? 'px-2.5 py-2' : 'px-2.5 py-1.5'
        )}
      >
        <item.icon
          className={classNames(
            iconClass,
            'mr-2.5 shrink-0',
            isActive
              ? (isDarkMode ? SELLERCLUB_NAV.iconActiveDark : 'text-orange-200')
              : (isDarkMode ? 'text-slate-400 group-hover:text-slate-200' : 'text-slate-500 group-hover:text-orange-700')
          )}
        />
        <span className="truncate">{item.name}</span>
        {item.badge && (
          <span className="ml-auto text-2xs font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400">
            {item.badge}
          </span>
        )}
      </a>
    );
  });
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [showStoreNameInHeader, setShowStoreNameInHeader] = useState(() => {
    try {
      const v = localStorage.getItem(HEADER_SHOW_STORE_KEY);
      return v !== '0';
    } catch {
      return true;
    }
  });
  const [campaignsExpanded, setCampaignsExpanded] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { selectedProfile } = useAmazonProfile();
  const { isDarkMode, toggleTheme } = useTheme();
  const isMobileViewport = useIsMobileViewport();

  // Enable optimized store switching (clears API cache, preloads data)
  useOptimizedStoreSwitch(selectedProfile);
  
  // Clear page cache when store changes (prevents showing old store's data)
  useClearPageCacheOnStoreChange(selectedProfile);

  const toggleSidebarExpanded = () => {
    setSidebarExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      if (!next) setCampaignsExpanded(false);
      else setCampaignsExpanded(true);
      return next;
    });
  };

  const permissions = user?.permissions || [];
  const platformAdmin = isPlatformAdmin(user?.role);
  const visibleNavigation = navigation.filter((item) => {
    if (item.platformAdminOnly && !platformAdmin) return false;
    if (item.userManagementOnly && !canAccessUserManagement(user?.role)) return false;
    const feature = NAV_FEATURE_MAP[item.name];
    if (feature && !platformAdmin && !userHasFeature(permissions, feature)) {
      return false;
    }
    return true;
  });

  const handleNavNavigate = (href, closeMobile = false) => {
    navigate(href);
    if (closeMobile) {
      setSidebarOpen(false);
    }
  };

  const handleCampaignsNavigate = (href, closeMobile = false) => {
    if (closeMobile || sidebarExpanded) {
      setCampaignsExpanded(true);
    }
    handleNavNavigate(href, closeMobile);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleHeaderStoreName = () => {
    setShowStoreNameInHeader((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(HEADER_SHOW_STORE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const headerBarClass = classNames(
    'relative z-50 flex-shrink-0 flex h-11 min-w-0 overflow-visible border-b transition-colors duration-300',
    isDarkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white border-slate-200/90 shadow-sm'
  );

  return (
    <div className={classNames(
      'h-screen flex flex-col overflow-hidden transition-colors duration-300',
      isDarkMode ? 'bg-slate-950' : 'bg-slate-100'
    )}>
      {/* Top bar — full width; sidebar starts below */}
      <header className={headerBarClass}>
        <button
          type="button"
          className={classNames(
            'px-4 border-r focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden',
            isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'
          )}
          onClick={() => setSidebarOpen(true)}
          aria-label="Open navigation menu"
        >
          <Bars3Icon className="h-5 w-5" aria-hidden />
        </button>

        <div className="flex-1 px-2 sm:px-4 flex justify-between items-center gap-2 min-w-0 overflow-hidden">
          <div className="flex items-center shrink min-w-0 max-w-[42%] sm:max-w-none">
            {isMobileViewport ? (
              <Logo variant="compact" linkToDashboard isDarkMode={isDarkMode} />
            ) : (
              <Logo variant="header" isDarkMode={isDarkMode} />
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1 justify-end overflow-visible">
            <button
              type="button"
              onClick={toggleHeaderStoreName}
              className={classNames(
                'p-1.5 rounded-lg border transition-colors hidden sm:flex',
                isDarkMode
                  ? 'border-slate-700 text-slate-400 hover:bg-slate-800'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50'
              )}
              title={showStoreNameInHeader ? 'Hide store name' : 'Show store name'}
              aria-label={showStoreNameInHeader ? 'Hide store name in header' : 'Show store name in header'}
              aria-pressed={showStoreNameInHeader}
            >
              {showStoreNameInHeader ? (
                <EyeSlashIcon className="h-4 w-4" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </button>

            <StoreSelector
              layout="header"
              showStoreName={showStoreNameInHeader && !isMobileViewport}
            />

            <button
              onClick={() => navigate('/stores')}
              className={classNames(
                'hidden lg:flex items-center px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors',
                isDarkMode
                  ? 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/10'
                  : 'text-slate-600 hover:text-orange-600 hover:bg-slate-50'
              )}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
              Stores
            </button>

            <button
              onClick={toggleTheme}
              className={classNames(
                'p-1.5 rounded-lg transition-colors duration-150',
                isDarkMode
                  ? 'bg-slate-800 hover:bg-slate-700 border border-slate-700'
                  : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
              )}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <svg className="icon-sm text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="icon-sm text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            <UserAccountMenu onLogout={handleLogout} />
          </div>
        </div>
      </header>

      <div className={classNames(
        'flex flex-1 min-h-0 overflow-hidden',
        isDarkMode ? 'bg-slate-950' : 'bg-slate-100'
      )}>
      {/* Mobile sidebar */}
      <div className={classNames(
        'fixed inset-x-0 top-11 bottom-0 flex z-40 md:hidden',
        sidebarOpen ? '' : 'hidden'
      )}>
        <div className="fixed inset-x-0 top-11 bottom-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        
        <div className={classNames(
          'relative flex-1 flex flex-col max-w-xs w-full backdrop-blur-lg border-r',
          isDarkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-slate-200'
        )}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <Logo variant="sidebar" isDarkMode={isDarkMode} className="border-r-0" />
            <nav className="mt-2 px-2 space-y-1">
              <SidebarNavItems
                items={visibleNavigation}
                pathname={location.pathname}
                isDarkMode={isDarkMode}
                campaignsExpanded={campaignsExpanded}
                onToggleCampaigns={() => setCampaignsExpanded((prev) => !prev)}
                onCampaignsNavigate={(href) => handleCampaignsNavigate(href, true)}
                navigate={(path) => handleNavNavigate(path, true)}
                size="mobile"
              />
              
              {/* Logout Button for Mobile */}
              <button
                onClick={handleLogout}
                className={classNames(
                  'w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200',
                  isDarkMode
                    ? 'text-red-400 hover:bg-red-500/10'
                    : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                )}
              >
                <ArrowRightOnRectangleIcon className="mr-2.5 flex-shrink-0 h-4 w-4" />
                Logout
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar — collapsed icon rail or expanded with labels */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div
          className={classNames(
            'flex flex-col transition-[width] duration-200 ease-out',
            sidebarExpanded ? 'w-56' : 'w-[3.75rem]'
          )}
        >
          <div
            className={classNames(
              'flex-1 flex flex-col overflow-y-auto overflow-x-visible border-r transition-colors duration-300',
              isDarkMode
                ? 'bg-slate-900 border-slate-800'
                : 'bg-white border-slate-200/90'
            )}
          >
            <nav className={classNames('flex-1 space-y-1', sidebarExpanded ? 'px-2 py-3' : 'px-1.5 py-3')}>
              <SidebarNavItems
                items={visibleNavigation}
                pathname={location.pathname}
                isDarkMode={isDarkMode}
                campaignsExpanded={campaignsExpanded}
                onToggleCampaigns={() => setCampaignsExpanded((prev) => !prev)}
                onCampaignsNavigate={(href) => handleCampaignsNavigate(href, false)}
                navigate={(path) => handleNavNavigate(path, false)}
                size="desktop"
                compact={!sidebarExpanded}
                showNavTooltips={!sidebarExpanded}
              />
            </nav>
            <div className={classNames('space-y-1', sidebarExpanded ? 'px-2 pb-3' : 'px-1.5 pb-3')}>
              <NavTooltip label="Collapse sidebar" show={!sidebarExpanded} isDarkMode={isDarkMode}>
                <button
                  type="button"
                  onClick={toggleSidebarExpanded}
                  className={classNames(
                    'w-full flex items-center rounded-lg transition-colors duration-200',
                    sidebarExpanded ? 'justify-start gap-2 px-2.5 py-2' : 'justify-center p-2.5',
                    isDarkMode
                      ? 'text-slate-400 hover:bg-slate-800/70 hover:text-slate-200'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                  )}
                  aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                  {sidebarExpanded ? (
                    <>
                      <ChevronLeftIcon className="h-5 w-5 shrink-0" />
                      <span className={classNames('truncate', typography.sidebarParent)}>Collapse</span>
                    </>
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 shrink-0" />
                  )}
                </button>
              </NavTooltip>
              <NavTooltip label="Logout" show={!sidebarExpanded} isDarkMode={isDarkMode}>
                <button
                  type="button"
                  onClick={handleLogout}
                  className={classNames(
                    'w-full flex items-center rounded-lg transition-colors duration-200',
                    sidebarExpanded ? 'justify-start gap-2 px-2.5 py-2' : 'justify-center p-2.5',
                    isDarkMode
                      ? 'text-red-400 hover:bg-red-500/10'
                      : 'text-red-600 hover:bg-red-50'
                  )}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 shrink-0" />
                  {sidebarExpanded && (
                    <span className={classNames('truncate', typography.sidebarParent)}>Logout</span>
                  )}
                </button>
              </NavTooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 min-h-0 overflow-hidden">
        <main className={classNames(
          'flex-1 relative overflow-y-auto focus:outline-none transition-colors duration-300',
          isDarkMode ? 'bg-slate-950 text-slate-100' : 'bg-transparent text-slate-900'
        )}>
          <PendingApprovalBanner />
          <SubscriptionStatusBanner />
          <div className="py-2 pb-20 app-density">
            <div className="w-full px-2 sm:px-3 lg:px-4 xl:px-5">
              <RouteErrorBoundary>
                <Outlet />
              </RouteErrorBoundary>
            </div>
          </div>
        </main>
        <DashboardAssistantChat />
      </div>
      </div>
    </div>
  );
}






